/**
 * LookerReport Component
 *
 * Displays Google Looker Studio reports, Google Docs, Google Sheets, and Google Drive files
 * in an embedded iframe. Handles URL transformations, scaling, refresh, and error states.
 *
 * Features:
 * - Supports Looker Studio, Google Docs, Google Sheets, and Google Drive
 * - Automatic URL transformation for embeddable formats
 * - Refresh functionality with cache-busting
 * - Fullscreen mode
 * - PDF export fallback for Google Sheets
 * - Error handling with retry capability
 */

import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Authorization } from '@/lib/authorization/authorization';
import { RootState } from '@/reducers/types';
import { useFullscreen } from '../../hooks/useFullScreen';
import { useReportManager } from './hooks';
import {
  decodeUrlEntities,
  getEmbedUrl,
  isTrustedUrl,
  isPdfUrl,
} from './utils';
import {
  DEFAULT_DOC_SCALE,
  LookerReportProps,
  TRUSTED_LOOKER_DOMAINS,
} from './types';
import {
  ActionButtons,
  LoadingOverlay,
  ReportIframe,
} from './components/components';

const LookerReport: React.FC<LookerReportProps> = ({
  reportUrl,
  onLoad,
  style,
  bare = false,
  floatingActions = true,
  active = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, handleFullscreenToggle } = useFullscreen(containerRef);
  const loggedUser = useSelector((state: RootState) => state.loggedUser);

  const decodedReportUrl = decodeUrlEntities(reportUrl.trim());
  const embedUrl = getEmbedUrl(decodedReportUrl);
  const isTrustedUrl_check =
    isTrustedUrl(decodedReportUrl, TRUSTED_LOOKER_DOMAINS) ||
    isTrustedUrl(embedUrl, TRUSTED_LOOKER_DOMAINS);

  const { isGoogleDoc, isGoogleSheet, docScale } = useDocumentType(
    decodedReportUrl,
    embedUrl
  );

  const {
    iframeSrc,
    reportLoaded,
    iframeError,
    handleRefresh,
    handleIframeLoad,
    handleIframeError,
  } = useReportManager({
    reportUrl,
    onLoad,
    isGoogleSheet,
  });

  const prevActiveRef = React.useRef<boolean | null>(null);
  useEffect(() => {
    if (prevActiveRef.current === null) {
      prevActiveRef.current = active;
      return;
    }
    if (!prevActiveRef.current && active) {
      // became active — only refresh automatically for published Google Sheets
      const isPublishedSheet =
        isGoogleSheet &&
        (decodedReportUrl.includes('/pub') ||
          embedUrl.includes('/pub') ||
          decodedReportUrl.includes('pubhtml') ||
          embedUrl.includes('pubhtml'));
      if (isPublishedSheet) {
        handleRefresh();
      }
    }
    prevActiveRef.current = active;
  }, [active, handleRefresh, isGoogleSheet, decodedReportUrl, embedUrl]);

  if (!isTrustedUrl_check) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 16,
          fontSize: '16px',
        }}
      >
        <div>
          {bare
            ? 'This report cannot be displayed — the content is unsupported or the URL is invalid.'
            : 'Invalid report URL'}
        </div>
      </div>
    );
  }

  const handleRetry = () => {
    handleRefresh();
  };

  const renderReportContent = () => (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        ...style,
      }}
    >
      {reportLoaded && (
        <ActionButtons
          isFullscreen={isFullscreen}
          onRefresh={handleRefresh}
          onToggleFullscreen={handleFullscreenToggle}
          sticky={floatingActions}
        />
      )}

      <ReportIframe
        src={iframeSrc}
        isGoogleDoc={isGoogleDoc}
        isGoogleSheet={isGoogleSheet}
        docScale={docScale}
        style={style}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        sandboxPermissions="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        pdfPadding={
          isPdfUrl(decodedReportUrl) ||
          isPdfUrl(embedUrl) ||
          iframeSrc.includes('gview')
        }
      />

      {!reportLoaded && (
        <LoadingOverlay
          hasError={iframeError !== null}
          errorMessage={iframeError || undefined}
          iframeSrc={decodedReportUrl}
          onRetry={handleRetry}
        />
      )}
    </div>
  );

  if (bare) {
    return renderReportContent();
  }

  return (
    <Authorization userId={loggedUser?.id} componentName="looker_report">
      {renderReportContent()}
    </Authorization>
  );
};

const useDocumentType = (reportUrl: string, embedUrl: string) => {
  const isGoogleDoc =
    reportUrl.includes('docs.google.com/document') ||
    embedUrl.includes('docs.google.com/document') ||
    embedUrl.includes('docs.google.com/gview');

  const isGoogleSheet =
    reportUrl.includes('spreadsheets') || embedUrl.includes('spreadsheets');

  // Only scale Google Docs; sheets render at natural size. Treat gview as Google embed.
  const docScale = isGoogleDoc ? DEFAULT_DOC_SCALE : 1;
  return { isGoogleDoc, isGoogleSheet, docScale };
};

export default LookerReport;
